import './App.css';
import React, { useEffect, Suspense } from 'react';
import Navbar from './components/navbar/navbar';
import Footer from './components/footer/footer';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { HelmetProvider } from 'react-helmet-async';
import AccessibilityPanel from './components/accessibility/AccessibilityPanel';
import PageTitle from './components/seo/PageTitle';
import './components/accessibility/accessibility.css';

// Lazy load components to improve performance
const Home = React.lazy(() => import('./pages/home'));
const WhoWeAre = React.lazy(() => import('./pages/whoWeAre'));
const ConnectWithUs = React.lazy(() => import('./pages/connectWithUs'));
const Programmes = React.lazy(() => import('./pages/programmes'));
const Projects = React.lazy(() => import('./pages/projects'));
const EventsAndNews = React.lazy(() => import('./pages/eventsAndNews'));
const WalkWithUs = React.lazy(() => import('./pages/walkWithUs'));
const Donate = React.lazy(() => import('./pages/donate'));

// ScrollToTop component to ensure page starts at the top when navigating
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// PageTransition component to handle page animations
const PageTransition = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Check if user prefers reduced motion
    const prefersReducedMotion = document.body.classList.contains('reduced-motion');
    
    if (!prefersReducedMotion) {
      // Fade in animation for page content
      gsap.fromTo(
        ".page-content",
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }
      );
    } else {
      // For users who prefer reduced motion, simply make content visible without animation
      gsap.set(".page-content", { opacity: 1, y: 0 });
    }
  }, [location]);

  return <div className="page-content">{children}</div>;
};

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <PageTitle />
      <Navbar />
      <PageTransition>
        <Suspense fallback={
          <div className="flex justify-center items-center h-screen">
            <div role="status" aria-live="polite" className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-base-green mx-auto"></div>
              <p className="mt-4 text-gray-700">Loading content...</p>
            </div>
          </div>
        }>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/WhoWeAre" element={<WhoWeAre />} />
            <Route path="/ConnectWithUs" element={<ConnectWithUs />} />
            <Route path="/Programmes" element={<Programmes />} />
            <Route path="/Projects" element={<Projects />} />
            <Route path="/EventsAndNews" element={<EventsAndNews />} />
            <Route path="/WalkWithUs" element={<WalkWithUs />} />
            <Route path="/Donate" element={<Donate />} />
          </Routes>
        </Suspense>
      </PageTransition>
      <Footer />
      <AccessibilityPanel />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="min-h-screen bg-white text-gray-800">
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
